import React from 'react';
import { Chip, FONT_MONO, FONT_SERIF } from './landingData';
import { ComposerCard, ComposerIdeas, type ComposerFile, type Platform, type Product, type SourceSite, type VideoAspect } from './Composer';
import { HeroFloaters } from './HeroFloaters';
import { fmtNum } from './format';

export type { ComposerFile, Platform, Product, SourceSite, VideoAspect };

interface Props {
    nav: React.ReactNode;
    prompt: string;
    onPrompt: (v: string) => void;
    onSubmit: () => void;
    submitting: boolean;
    platform: Platform;
    onPlatform: (p: Platform) => void;
    /** null = anonymous visitor (credits pill hidden) */
    credits: number | null;
    onOpenTopUp: () => void;
    chips: Chip[];
    onShuffle: () => void;
    onFillChip: (chip: Chip) => void;
    placeholderIndex: number;
    placeholderText: string;
    /** Text of the "NEW" pill and what happens when it is clicked. */
    announcement: string;
    onAnnouncement: () => void;
    product: Product;
    onProduct: (p: Product) => void;
    aspect: VideoAspect;
    onAspect: (a: VideoAspect) => void;
    files: ComposerFile[];
    uploading: boolean;
    onPickFiles: (files: FileList) => void;
    onRemoveFile: (id: string) => void;
    sourceSites: SourceSite[];
    source: SourceSite | null;
    onSource: (s: SourceSite | null) => void;
}

const tw = (v: string) => ({ textWrap: v } as React.CSSProperties);

const Arrow = ({ size = 15, sw = 2.4 }: { size?: number; sw?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h13M13 6l6 6-6 6" /></svg>
);

export const HeroSection: React.FC<Props> = (p) => {
    const { nav, prompt, onPrompt, onSubmit, submitting, platform, onPlatform, credits, onOpenTopUp, chips, onShuffle, onFillChip, placeholderIndex, placeholderText, announcement, onAnnouncement, product, onProduct, aspect, onAspect, files, uploading, onPickFiles, onRemoveFile, sourceSites, source, onSource } = p;
    const isVideo = product === 'video';

    return (
        <div className="hv-sheet" style={{ position: 'relative', zIndex: 1, margin: '14px 14px 52px', borderRadius: 26, overflow: 'clip', background: 'var(--hv-page)', boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset,0 26px 50px -40px var(--hv-shadow)' }}>
            <section data-screen-label="Hero" style={{ position: 'relative', overflow: 'hidden', minHeight: 'calc(100vh - 28px)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.85, backgroundImage: 'radial-gradient(circle,var(--hv-line-2) 1.3px,transparent 1.9px)', backgroundSize: '24px 24px', maskImage: 'radial-gradient(ellipse 60% 50% at 50% 0%,black,transparent)', WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 0%,black,transparent)' }} />
                <div style={{ position: 'absolute', top: -200, left: '50%', width: 820, height: 480, marginLeft: -410, borderRadius: '50%', background: 'radial-gradient(closest-side,color-mix(in srgb,var(--brand,#13422C) 10%,transparent),transparent)', filter: 'blur(34px)', pointerEvents: 'none' }} />

                {nav}

                <HeroFloaters product={isVideo ? 'video' : 'web'} />

                <div className="hv-shell hv-hero-pad" style={{ position: 'relative', zIndex: 2, flex: 1, width: '100%', boxSizing: 'border-box', maxWidth: 1280, margin: '0 auto', padding: '44px 28px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <a href="#templates" data-track="hero_announcement" onClick={(e) => { e.preventDefault(); onAnnouncement(); }} className="hv-h-brand-bd" style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 10, height: 38, padding: '0 16px 0 5px', borderRadius: 999, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 12px 24px -20px var(--hv-shadow)', fontSize: 13, fontWeight: 600, color: 'var(--hv-ink)', animation: 'hvRise 0.8s cubic-bezier(0.22,0.9,0.24,1) 0.08s both' }}>
                        <span style={{ fontFamily: FONT_MONO, height: 28, display: 'flex', alignItems: 'center', padding: '0 10px', borderRadius: 999, background: 'var(--brand,#13422C)', color: '#FFFDF7', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em' }}>NEW</span>
                        {announcement}
                        <Arrow size={14} sw={2.3} />
                    </a>

                    <h1 className="hv-hero-h1" style={{ margin: '22px 0 0', maxWidth: 900, fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 'clamp(36px,5.5vw,78px)', lineHeight: 1.1, letterSpacing: '-0.01em', wordSpacing: '0.06em', textAlign: 'center', animation: 'hvRise 0.85s cubic-bezier(0.22,0.9,0.24,1) 0.14s both', ...tw('balance') }}>
                        What will you{' '}
                        <span style={{ position: 'relative', display: 'inline-block', fontStyle: 'italic' }}>
                            make
                            <svg width="100%" height="16" viewBox="0 0 200 16" preserveAspectRatio="none" style={{ position: 'absolute', left: '-4%', bottom: -2, width: '108%', height: 16, overflow: 'visible' }} aria-hidden="true"><path d="M2 10C40 3 78 13 116 7c32-5 58 2 82-3" fill="none" stroke="#2E9E64" strokeWidth="5" strokeLinecap="round" /></svg>
                        </span>{' '}
                        today?
                    </h1>

                    <p className="hv-hero-p" style={{ margin: '26px 0 0', maxWidth: 620, textAlign: 'center', fontSize: 15.5, lineHeight: 1.6, color: 'var(--hv-body)', animation: 'hvRise 0.85s cubic-bezier(0.22,0.9,0.24,1) 0.24s both', ...tw('balance') }}>
                        Just type out your idea. HyVay builds and hosts your website, or turns it into a promo video ready to post.
                    </p>

                    <ComposerCard
                        prompt={prompt} onPrompt={onPrompt} onSubmit={onSubmit} submitting={submitting}
                        platform={platform} onPlatform={onPlatform} credits={credits} onOpenTopUp={onOpenTopUp}
                        placeholderIndex={placeholderIndex} placeholderText={placeholderText}
                        product={product} onProduct={onProduct} aspect={aspect} onAspect={onAspect}
                        files={files} uploading={uploading} onPickFiles={onPickFiles} onRemoveFile={onRemoveFile}
                        sourceSites={sourceSites} source={source} onSource={onSource}
                        style={{ marginTop: 48, animation: 'hvPop 1s cubic-bezier(0.18,0.86,0.24,1) 0.34s both' }}
                    />

                    <ComposerIdeas chips={chips} onShuffle={onShuffle} onFillChip={onFillChip} product={product} style={{ marginTop: 48, animation: 'hvFade 1s ease 0.6s both' }} />
                </div>
            </section>
        </div>
    );
};

